import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PUBLIC_MOTION, resolveMotionFrom } from "./publicMotion";

// Static HTML has no animation runtime: render it fully visible for no-JS readers.
function MotionTag(as = "div") {
  return motion[as] ?? motion.div;
}

function canObserveViewport() {
  return typeof IntersectionObserver === "function";
}

function hiddenState(reduced, tone, y, fromOpacity, blur) {
  if (reduced) return { opacity: 1, y: 0, filter: "blur(0px)" };
  return resolveMotionFrom(tone, y, fromOpacity, blur);
}

function visibleState() {
  return { opacity: 1, y: 0, filter: "blur(0px)" };
}

function motionTransition(reduced, delay = 0) {
  return reduced
    ? { duration: 0 }
    : { duration: PUBLIC_MOTION.duration, ease: PUBLIC_MOTION.ease, delay };
}

function itemVariants(reduced, tone, y, fromOpacity, blur) {
  return {
    hidden: hiddenState(reduced, tone, y, fromOpacity, blur),
    show: {
      ...visibleState(),
      transition: motionTransition(reduced),
    },
  };
}

function groupTransition(stagger, delayChildren, reduced) {
  return {
    staggerChildren: reduced ? 0 : stagger,
    delayChildren: reduced ? 0 : delayChildren,
  };
}

export function Reveal({
  children,
  className,
  delay = 0,
  y,
  fromOpacity,
  blur,
  tone = "text",
  amount = PUBLIC_MOTION.viewportAmount,
  mode = "view",
  as = "div",
  ...rest
}) {
  const reduced = useReducedMotion() === true || typeof window === "undefined";
  const Tag = MotionTag(as);
  const hidden = hiddenState(reduced, tone, y, fromOpacity, blur);
  const visible = visibleState();
  const transition = motionTransition(reduced, delay);

  const trigger =
    mode === "load" || !canObserveViewport()
      ? { initial: hidden, animate: visible, transition }
      : {
          initial: hidden,
          whileInView: visible,
          viewport: { once: true, amount },
          transition,
        };

  return (
    <Tag className={className} {...trigger} {...rest}>
      {children}
    </Tag>
  );
}

export function RevealGroup({
  children,
  className,
  stagger = PUBLIC_MOTION.stagger,
  delayChildren = PUBLIC_MOTION.delayChildren,
  amount = PUBLIC_MOTION.viewportAmount,
  mode = "view",
  as = "div",
  ...rest
}) {
  const reduced = useReducedMotion() === true || typeof window === "undefined";
  const Tag = MotionTag(as);
  const variants = {
    hidden: {},
    show: { transition: groupTransition(stagger, delayChildren, reduced) },
  };

  const trigger =
    mode === "load" || !canObserveViewport()
      ? { initial: "hidden", animate: "show" }
      : {
          initial: "hidden",
          whileInView: "show",
          viewport: { once: true, amount },
        };

  return (
    <Tag className={className} variants={variants} {...trigger} {...rest}>
      {children}
    </Tag>
  );
}

export function RevealStagger({
  children,
  className,
  stagger = PUBLIC_MOTION.stagger,
  delayChildren = 0,
  as = "div",
  ...rest
}) {
  const reduced = useReducedMotion() === true || typeof window === "undefined";
  const Tag = MotionTag(as);

  return (
    <Tag
      className={className}
      variants={{
        hidden: {},
        show: { transition: groupTransition(stagger, delayChildren, reduced) },
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function RevealItem({
  children,
  className,
  y,
  fromOpacity,
  blur,
  tone = "text",
  as = "div",
  ...rest
}) {
  const reduced = useReducedMotion() === true || typeof window === "undefined";
  const Tag = MotionTag(as);

  return (
    <Tag
      className={className}
      variants={itemVariants(reduced, tone, y, fromOpacity, blur)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function HeroBackdrop({ className, children }) {
  const reduced = useReducedMotion() === true || typeof window === "undefined";

  return (
    <motion.div
      aria-hidden="true"
      className={className}
      initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0.9, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reduced
          ? { duration: 0 }
          : { duration: 0.7, ease: PUBLIC_MOTION.ease }
      }
    >
      {children}
    </motion.div>
  );
}
